import PageLayout from "@/components/PageLayout";
import ProjectForm, { ProjectFormValues } from "@/components/ProjectForm";
import { useProject, useUpdateProject } from "@/hooks/useProjects";
import { useProjectPermissions } from "@/hooks/useProjectPermissions";
import { ArrowLeft } from "lucide-react";
import { Navigate, useNavigate, useParams } from "react-router-dom";

const EditProject = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: project, isLoading } = useProject(id!);
  const { canEditProject } = useProjectPermissions();
  const updateProject = useUpdateProject();

  const handleSubmit = async (values: ProjectFormValues) => {
    try {
      await updateProject.mutateAsync({
        id: id!,
        name: values.name,
        code: values.code,
        kantataProjectId: values.kantataProjectId,
        clientIds: values.clientIds,
        therapyArea: values.therapyArea,
        condition: values.condition,
        objective: values.objective,
        projectType: values.projectType,
        model: values.model,
        dataRetentionDefault: values.dataRetentionDefault,
        memberIds: values.memberIds,
      });

      navigate(-1);
    } catch {
      // error surfaced via updateProject.isError
    }
  };

  if (isLoading || !project) {
    return (
      <PageLayout mainClassName="max-w-3xl mx-auto px-6 py-8">
        <div className="text-muted-foreground py-8">Loading project...</div>
      </PageLayout>
    );
  }

  if (!canEditProject(project)) {
    return <Navigate to={`/projects/${id}`} replace />;
  }

  return (
    <PageLayout mainClassName="max-w-3xl mx-auto px-6 py-8">
      <button type="button" onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-brand-teal hover:underline mb-6">
        <ArrowLeft className="w-4 h-4" />
        Back
      </button>

      <h1 className="text-2xl font-bold text-brand-navy mb-2">Update Project Setup</h1>
      <p className="text-muted-foreground mb-8">Edit project information below. Changes will be saved immediately.</p>

      <ProjectForm
        isEditing={true}
        ownerId={project.createdBy ?? undefined}
        defaultValues={{
          name: project.name,
          code: project.code,
          kantataProjectId: project.kantataProjectId ?? "",
          kantataProjectName: project.code,
          clientIds: project.clientIds,
          clientNames: project.clientNames,
          projectType: project.projectType,
          therapyArea: project.therapyArea,
          condition: project.condition,
          objective: project.objective,
          model: project.model,
          dataRetentionDefault: project.dataRetentionDefault,
          memberIds: project.memberIds,
        }}
        onSubmit={handleSubmit}
        isSubmitting={updateProject.isPending}
        submitLabel="Save Changes"
        onCancel={() => navigate(-1)}
      />
    </PageLayout>
  );
};

export default EditProject;
